import type { ArticulatedNote, TunedNote } from "./types";
import type { Instrument } from "../data/instruments";
import { gayageumDecayWithNonghyeon } from "./pitch";

export {
  clampSitarMeend,
  daegeumCheongBuzzAmount,
  erhuPortamentoLogistic,
  erhuPortamentoSmoothstep,
  gayageumDecayWithNonghyeon,
  gayageumNonghyeonCurve,
  tablaBayanPressureBend,
  tablaBayanReleaseFall
} from "./pitch";

export interface ArticulationControlState {
  bowPressure: number;
  bowSpeed: number;
  erhuPortamentoShape: "smoothstep" | "logistic";
  breathPressure: number;
  cheongTension: number;
  cheongExposure: number;
  buzzAmount: number;
  bayanPressure: number;
  meendMode: "beginner" | "standard" | "expert";
  neyMicrobend: number;
  mouthShapeY: number;
  circularHold: boolean;
  vocalization: number;
  ombakDepth: number;
  saronTuningHint: "genericSlendroApprox" | "genericPelogApprox";
  nonghyeonDepth: number;
  microBendAmount: number;
}

export const defaultArticulationControlState: ArticulationControlState = {
  bowPressure: 0.5,
  bowSpeed: 0.45,
  erhuPortamentoShape: "smoothstep",
  breathPressure: 0.55,
  cheongTension: 0.5,
  cheongExposure: 0.52,
  buzzAmount: 0.35,
  bayanPressure: 0,
  meendMode: "standard",
  neyMicrobend: 0.35,
  mouthShapeY: 0.35,
  circularHold: false,
  vocalization: 0.28,
  ombakDepth: 0.45,
  saronTuningHint: "genericSlendroApprox",
  nonghyeonDepth: 0.5,
  microBendAmount: 0.35
};

export class ArticulationEngine {
  shape(
    note: TunedNote,
    instrument: Instrument,
    controls: ArticulationControlState = defaultArticulationControlState
  ): ArticulatedNote {
    const family = instrument.classification.family;
    const isBreath = family === "breathy_transverse_flute" || family === "breathy_end_blown_flute";
    const isPercussion = family === "membrane_drum";
    const isMetal = family === "bronze_idiophone" || family === "metal_idiophone";
    const isLamellophone = family === "lamellophone";
    const isHarpLute = family === "plucked_harp_lute";
    const nonghyeonDepth =
      note.ornament === "nonghyeon"
        ? 40 + controls.nonghyeonDepth * 140
        : note.ornament === "bend"
          ? 45
          : 0;
    const baseDecay = isPercussion ? 0.08 : isBreath ? 0.18 : isMetal ? 0.42 : isLamellophone ? 0.26 : 0.18;
    const baseRelease = isPercussion ? 0.16 : isBreath ? 0.28 : isMetal ? 1.2 : isHarpLute ? 0.72 : 0.42;
    const gayageumEnvelope =
      instrument.id === "gayageum"
        ? gayageumDecayWithNonghyeon(baseDecay, baseRelease, nonghyeonDepth)
        : null;

    return {
      ...note,
      attack: isPercussion ? 0.002 : isBreath ? 0.045 : instrument.id === "erhu" ? 0.03 : 0.005,
      decay: gayageumEnvelope?.decay ?? baseDecay,
      sustain: isPercussion ? 0.01 : isBreath ? 0.72 : isMetal ? 0.28 : isLamellophone ? 0.2 : 0.32,
      release: gayageumEnvelope?.release ?? baseRelease,
      brightness:
        instrument.id === "erhu"
          ? 0.42 + controls.bowPressure * 0.46
          : isBreath
            ? 0.38 + controls.breathPressure * 0.34
            : isMetal
              ? 0.86
              : 0.56,
      resonance: isPercussion ? 0.42 : isMetal ? 0.82 : isHarpLute ? 0.78 : 0.64
    };
  }
}
